import { useCallback } from "react";

const allEventParams = {
  alpaca_source: "nrma-member-benefits",
};

const useGtagEffects = () => {
  const getGtag = useCallback(() => {
    if (window != null && window.gtag != null) {
      return window.gtag as Gtag;
    }
  }, []);

  const getDebug = useCallback(() => {
    if (window != null && "gtagDebug" in window && window.gtagDebug === true) {
      return true;
    }
    return false;
  }, []);

  const getDataLayer = useCallback(() => {
    if (
      window != null &&
      "dataLayer" in window &&
      Array.isArray(window.dataLayer)
    ) {
      return window.dataLayer as any[];
    }
    return null;
  }, []);

  const triggerEvent = useCallback(
    (eventName: EventNames, params: EventParams) => {
      const params2 = {
        ...allEventParams,
        ...params,
      };
      if (getDebug() === true) {
        console.log("event", eventName, params2);
      }
      /* At request of Tanzir, we are using the datalayer, not the gtag */
      // const gtag = getGtag();
      // if (gtag != null) {
      //   gtag("event", eventName, params2);
      // }
      const dataLayer = getDataLayer();
      if (dataLayer != null) {
        dataLayer.push({
          event: eventName,
          ...params2,
        });
      }
    },
    [/*getGtag, */ getDebug, getDataLayer]
  );

  const triggerCustomEvent = useCallback(
    (action: string, params: EventParams & Record<string, any>) => {
      const params2 = {
        ...allEventParams,
        ...params,
      };
      if (getDebug() === true) {
        console.log("event", action, params2);
      }
      const gtag = getGtag();
      if (gtag != null) {
        gtag("event", action, params2);
      }
    },
    [getGtag, getDebug]
  );

  return { triggerEvent, triggerCustomEvent };
};

export default useGtagEffects;

interface GtagCommands {
  event: [
    eventName: EventNames | (string & {}),
    eventParams?: ControlParams | EventParams | CustomParams
  ];
}

interface CustomParams {
  [key: string]: any;
}

interface ControlParams {
  groups?: string | string[] | undefined;
  send_to?: string | string[] | undefined;
  event_callback?: (() => void) | undefined;
  event_timeout?: number | undefined;
}

interface Gtag {
  <Command extends keyof GtagCommands>(
    command: Command,
    ...args: GtagCommands[Command]
  ): void;
}

type EventNames =
  | "add_payment_info"
  | "add_shipping_info"
  | "add_to_cart"
  | "add_to_wishlist"
  | "begin_checkout"
  | "checkout_progress"
  | "earn_virtual_currency"
  | "exception"
  | "generate_lead"
  | "join_group"
  | "level_end"
  | "level_start"
  | "level_up"
  | "login"
  | "page_view"
  | "post_score"
  | "purchase"
  | "refund"
  | "remove_from_cart"
  | "screen_view"
  | "search"
  | "select_content"
  | "select_item"
  | "select_promotion"
  | "set_checkout_option"
  | "share"
  | "sign_up"
  | "spend_virtual_currency"
  | "tutorial_begin"
  | "tutorial_complete"
  | "unlock_achievement"
  | "timing_complete"
  | "view_cart"
  | "view_item"
  | "view_item_list"
  | "view_promotion"
  | "view_search_results";

interface EventParams {
  checkout_option?: string | undefined;
  checkout_step?: number | undefined;
  content_id?: string | undefined;
  content_type?: string | undefined;
  coupon?: string | undefined;
  currency?: string | undefined;
  description?: string | undefined;
  fatal?: boolean | undefined;
  items?: Item[] | undefined;
  method?: string | undefined;
  number?: string | undefined;
  promotions?: Promotion[] | undefined;
  screen_name?: string | undefined;
  search_term?: string | undefined;
  shipping?: Currency | undefined;
  tax?: Currency | undefined;
  transaction_id?: string | undefined;
  value?: number | undefined;
  event_label?: string | undefined;
  event_category?: string | undefined;
}
type Currency = string | number;

interface Item {
  item_id?: string | undefined;
  item_name?: string | undefined;
  affiliation?: string | undefined;
  coupon?: string | undefined;
  currency?: string | undefined;
  creative_name?: string | undefined;
  creative_slot?: string | undefined;
  discount?: Currency | undefined;
  index?: number | undefined;
  item_brand?: string | undefined;
  item_category?: string | undefined;
  item_category2?: string | undefined;
  item_category3?: string | undefined;
  item_category4?: string | undefined;
  item_category5?: string | undefined;
  item_list_id?: string | undefined;
  item_list_name?: string | undefined;
  item_variant?: string | undefined;
  location_id?: string | undefined;
  price?: Currency | undefined;
  promotion_id?: string | undefined;
  promotion_name?: string | undefined;
  quantity?: number | undefined;
}

interface Promotion {
  creative_name?: string | undefined;
  creative_slot?: string | undefined;
  promotion_id?: string | undefined;
  promotion_name?: string | undefined;
}
